// === OVERLAY DE CRASH ===
// Filet de sécurité affiché par-dessus le canvas quand le jeu plante.
// Donne au joueur 3 actions : retour menu, signaler le crash (bug report
// pré-rempli avec la stack), copier les détails techniques.
//
// Singleton : un seul overlay actif à la fois (les crashes en rafale sont
// déjà filtrés par CrashHandler.dedupe + burst, mais on double-vérifie ici).

import type { CrashInfo } from '../systems/CrashHandler';
import { formatCrashForBugReport } from '../systems/CrashHandler';
import { showBugReportOverlay } from './BugReportOverlay';
import { GAME_VERSION } from '../systems/MetaManager';
import { captureCanvas } from '../systems/BugReportContext';

let activeOverlay: HTMLDivElement | null = null;

export interface CrashOverlayOptions {
  /** Clé de la scène Phaser active au moment du crash. */
  currentScene: string;
  /** Appelé quand le joueur clique « Retour au menu ». */
  onReturnToMenu: () => void;
  /** Canvas Phaser pour capture (optionnel). */
  canvas?: HTMLCanvasElement;
  /** Ligne de contexte run optionnelle (étage, équipe, or...). */
  contextLine?: string;
}

/**
 * Affiche l'overlay de crash. Si un overlay est déjà visible, ne fait rien
 * (le 1er crash a la priorité, les suivants sont silencieux côté UI).
 */
export function showCrashOverlay(info: CrashInfo, opts: CrashOverlayOptions): void {
  if (activeOverlay) return;

  const overlay = document.createElement('div');
  activeOverlay = overlay;
  overlay.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'width:100%', 'height:100%',
    'background:rgba(0,0,0,0.92)', 'z-index:10000',
    'display:flex', 'align-items:center', 'justify-content:center',
  ].join(';');

  // Bloque les events derrière l'overlay (mêmes raisons que BugReportOverlay :
  // Phaser écoute au niveau document, on stoppe la propagation à la bulle).
  const stopAll = (e: Event) => e.stopPropagation();
  ['keydown', 'keyup', 'keypress',
    'mousedown', 'mouseup', 'click',
    'pointerdown', 'pointerup', 'pointermove',
    'touchstart', 'touchend', 'touchmove',
    'wheel'].forEach(type => {
    overlay.addEventListener(type, stopAll);
  });

  const box = document.createElement('div');
  box.style.cssText = [
    'background:#1a1a2e', 'border:2px solid #e74c3c', 'border-radius:8px',
    'padding:24px 28px', 'width:460px', 'max-width:92vw', 'max-height:88vh',
    'overflow-y:auto', 'font-family:monospace', 'color:#ecf0f1',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = '💥 Le jeu a planté';
  title.style.cssText = 'font-size:18px;color:#e74c3c;font-weight:bold;margin-bottom:6px;';

  const subtitle = document.createElement('div');
  subtitle.textContent = "Ne perds pas tes progrès — la sauvegarde du camp est intacte. Reviens au menu pour reprendre.";
  subtitle.style.cssText = 'font-size:11px;color:#bdc3c7;margin-bottom:16px;line-height:1.5;';

  // Bloc message court (toujours visible)
  const msgBox = document.createElement('div');
  msgBox.style.cssText = [
    'background:#0d1117', 'border:1px solid #3a536b', 'border-radius:4px',
    'padding:8px 10px', 'font-size:11px', 'color:#f39c12',
    'margin-bottom:10px', 'word-break:break-word',
  ].join(';');
  msgBox.textContent = info.message;

  // Détails techniques (repliés par défaut)
  const details = document.createElement('details');
  details.style.cssText = 'margin-bottom:14px;font-size:10px;';
  const summary = document.createElement('summary');
  summary.textContent = 'Voir les détails techniques';
  summary.style.cssText = 'cursor:pointer;color:#7f8c8d;margin-bottom:6px;outline:none;';
  details.appendChild(summary);

  const techBlock = document.createElement('pre');
  techBlock.style.cssText = [
    'background:#0d1117', 'border:1px solid #2c3e50', 'border-radius:4px',
    'padding:8px 10px', 'font-size:10px', 'color:#7f8c8d',
    'max-height:160px', 'overflow:auto', 'white-space:pre-wrap',
    'word-break:break-word', 'margin:0',
  ].join(';');
  techBlock.textContent = buildTechBlock(info, opts);
  details.appendChild(techBlock);

  // Feedback line (copie etc.)
  const feedback = document.createElement('div');
  feedback.style.cssText = 'font-size:11px;margin-bottom:10px;min-height:16px;color:#2ecc71;';

  // Boutons
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;';

  const menuBtn = mkBtn('🏴‍☠️ Retour au menu', '#2ecc71', '#1a2a1a', 2);
  const reportBtn = mkBtn('🐛 Signaler', '#e74c3c', '#2a1a1a', 1);
  const copyBtn = mkBtn('📋 Copier', '#3498db', '#1a2a3a', 1);

  const close = () => {
    if (activeOverlay === overlay) activeOverlay = null;
    overlay.remove();
  };

  menuBtn.addEventListener('click', () => {
    try {
      opts.onReturnToMenu();
    } finally {
      close();
    }
  });

  copyBtn.addEventListener('click', async () => {
    const text = buildTechBlock(info, opts);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        feedback.textContent = '✅ Détails copiés dans le presse-papiers';
        feedback.style.color = '#2ecc71';
      } else {
        // Fallback : sélection manuelle dans le pre
        techBlock.focus();
        feedback.textContent = "ℹ️ Sélectionne le texte ci-dessus pour le copier";
        feedback.style.color = '#f39c12';
      }
    } catch {
      feedback.textContent = '⚠️ Impossible de copier — sélectionne le texte ci-dessus';
      feedback.style.color = '#e67e22';
    }
  });

  reportBtn.addEventListener('click', async () => {
    // Capture du canvas si dispo (avant d'ouvrir le formulaire)
    let screenshot: Blob | null = null;
    if (opts.canvas) {
      screenshot = await captureCanvas(opts.canvas).catch(() => null);
    }
    // Ferme l'overlay de crash (le bug report prend le relais)
    close();
    showBugReportOverlay(
      opts.currentScene,
      undefined,
      opts.contextLine,
      screenshot,
      formatCrashForBugReport(info),
    );
  });

  btnRow.appendChild(menuBtn);
  btnRow.appendChild(reportBtn);
  btnRow.appendChild(copyBtn);

  box.appendChild(title);
  box.appendChild(subtitle);
  box.appendChild(msgBox);
  box.appendChild(details);
  box.appendChild(feedback);
  box.appendChild(btnRow);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

/** Indique si un overlay de crash est actuellement visible. */
export function isCrashOverlayVisible(): boolean {
  return activeOverlay !== null;
}

/** Ferme l'overlay s'il est visible (utile pour les tests). */
export function closeCrashOverlay(): void {
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
  }
}

function buildTechBlock(info: CrashInfo, opts: CrashOverlayOptions): string {
  const lines: string[] = [];
  lines.push(`Type     : ${info.kind === 'rejection' ? 'Promise rejection' : 'Erreur JS'}`);
  lines.push(`Message  : ${info.message}`);
  if (info.source) lines.push(`Source   : ${info.source}`);
  lines.push(`Scène    : ${opts.currentScene}`);
  lines.push(`Version  : v${GAME_VERSION}`);
  if (opts.contextLine) lines.push(`Run      : ${opts.contextLine}`);
  if (info.stack) {
    lines.push('');
    lines.push('Stack :');
    lines.push(info.stack.split('\n').slice(0, 14).join('\n'));
  }
  return lines.join('\n');
}

function mkBtn(label: string, color: string, bg: string, flex: number): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = `[ ${label} ]`;
  b.style.cssText = [
    `flex:${flex}`, 'min-width:100px', `background:${bg}`,
    `border:1px solid ${color}`, 'border-radius:4px',
    `color:${color}`, 'font-family:monospace', 'font-size:12px',
    'padding:9px 6px', 'cursor:pointer',
  ].join(';');
  // Hover discret : éclaircit le texte
  b.addEventListener('mouseover', () => { b.style.filter = 'brightness(1.4)'; });
  b.addEventListener('mouseout', () => { b.style.filter = ''; });
  return b;
}
